import React from 'react';
import { Plus } from 'lucide-react';

import { Aide } from '../ui/aide';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

/**
 * Correspondance colonne -> champ, modifiable. Chaque changement est envoyé au
 * serveur, qui recalcule tout le brouillon.
 *
 * Une entrée = une feuille lue comme des camions, des chauffeurs, ou ignorée.
 * Une même feuille peut porter deux entrées (camions et chauffeurs).
 */

const AUCUNE = '__aucune__';
const NATURES_LIBELLES = {
  trucks: 'Camions',
  drivers: 'Chauffeurs',
  lot_trucks: 'Camions du lot',
};
/** Natures proposées : celles du catalogue du travail (flotte : camions et chauffeurs ; lot : camions du lot). */
const naturesDuTravail = (job) => [
  ...Object.keys(job.catalog || { trucks: [], drivers: [] }).map((value) => ({ value, label: NATURES_LIBELLES[value] || value })),
  { value: 'ignore', label: 'Ignorer cette feuille' },
];
const AVEC_UNITE = ['trucks', 'lot_trucks'];
const UNITES = [
  { value: 'L', label: 'Litres' },
  { value: 'm3', label: 'Mètres cubes' },
];
const FORMATS_DATE = [
  { value: 'DMY', label: 'Jour / mois / année' },
  { value: 'MDY', label: 'Mois / jour / année' },
  { value: 'YMD', label: 'Année / mois / jour' },
];

const Liste = ({ valeur, options, onChange, disabled, label, testid }) => (
  <Select value={valeur} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger aria-label={label} data-testid={testid} className="h-8 text-xs">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {options.map((option) => (
        <SelectItem key={option.value} value={option.value}>
          {option.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const CorrespondanceColonnes = ({ job, onChange, disabled }) => {
  const entrees = job.mapping?.entries || [];
  const feuilles = job.sheets || [];
  const natures = naturesDuTravail(job);
  // Une feuille ne porte à la fois camions et chauffeurs que pour l'import de la flotte.
  const flotte = !job.catalog || Boolean(job.catalog.trucks && job.catalog.drivers);

  const remplacer = (position, changements) =>
    onChange(entrees.map((entree, index) => (index === position ? { ...entree, ...changements } : entree)));

  return (
    <section className="space-y-3" data-testid="fleet-import-mapping" aria-label="Correspondance des colonnes">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-semibold">Correspondance des colonnes</h2>
        <Aide titre="Correspondance des colonnes">
          Choisissez pour chaque champ la colonne de votre fichier. Le brouillon se recalcule à chaque changement.
        </Aide>
        {job.mapping_source === 'ai' && (
          <span className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs">Préparée par l’IA</span>
        )}
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        {entrees.map((entree, position) => {
          const feuille = feuilles[entree.sheet];
          if (!feuille) return null;
          const colonnes = [
            { value: AUCUNE, label: 'Aucune colonne' },
            ...feuille.headers.map((titre, index) => ({ value: String(index), label: titre })),
          ];
          const champs = job.catalog?.[entree.kind] || [];
          const autre = entree.kind === 'trucks' ? 'drivers' : 'trucks';
          const aDejaAutre = !flotte || entrees.some((e) => e.sheet === entree.sheet && e.kind === autre);
          return (
            <div
              key={`${entree.sheet}-${entree.kind}`}
              className="space-y-3 rounded-xl border border-border bg-card p-3"
              data-testid={`fleet-import-entry-${entree.sheet}-${entree.kind}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-medium">{feuille.name}</p>
                  <p className="text-xs tabular-nums text-muted-foreground">{feuille.row_count} lignes</p>
                </div>
                <div className="w-48">
                  <Liste
                    valeur={entree.kind}
                    options={natures}
                    label={`Contenu de la feuille ${feuille.name}`}
                    testid={`fleet-import-kind-${entree.sheet}-${entree.kind}`}
                    disabled={disabled}
                    onChange={(valeur) => remplacer(position, { kind: valeur, columns: {} })}
                  />
                </div>
              </div>
              {entree.kind !== 'ignore' && (
                <div className="grid gap-2 sm:grid-cols-2">
                  {champs.map((champ) => (
                    <label key={champ.key} className="space-y-1 text-xs font-medium">
                      <span>
                        {champ.label}
                        {champ.required ? ' *' : ''}
                      </span>
                      <Liste
                        valeur={entree.columns?.[champ.key] === undefined ? AUCUNE : String(entree.columns[champ.key])}
                        options={colonnes}
                        label={champ.label}
                        testid={`fleet-import-field-${entree.sheet}-${entree.kind}-${champ.key}`}
                        disabled={disabled}
                        onChange={(valeur) => {
                          const colonnesChoisies = { ...entree.columns };
                          if (valeur === AUCUNE) delete colonnesChoisies[champ.key];
                          else colonnesChoisies[champ.key] = Number(valeur);
                          remplacer(position, { columns: colonnesChoisies });
                        }}
                      />
                    </label>
                  ))}
                  {AVEC_UNITE.includes(entree.kind) && (
                    <label className="space-y-1 text-xs font-medium">
                      <span>{entree.kind === 'trucks' ? 'Unité de la capacité' : 'Unité de la quantité'}</span>
                      <Liste
                        valeur={entree.capacity_unit || 'L'}
                        options={UNITES}
                        label={entree.kind === 'trucks' ? 'Unité de la capacité' : 'Unité de la quantité'}
                        testid={`fleet-import-unit-${entree.sheet}`}
                        disabled={disabled}
                        onChange={(valeur) => remplacer(position, { capacity_unit: valeur })}
                      />
                    </label>
                  )}
                  {entree.kind === 'trucks' && (
                    <label className="space-y-1 text-xs font-medium">
                      <span>Format des dates</span>
                      <Liste
                        valeur={entree.date_format || 'DMY'}
                        options={FORMATS_DATE}
                        label="Format des dates"
                        testid={`fleet-import-date-${entree.sheet}`}
                        disabled={disabled}
                        onChange={(valeur) => remplacer(position, { date_format: valeur })}
                      />
                    </label>
                  )}
                </div>
              )}
              {entree.kind !== 'ignore' && !aDejaAutre && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={disabled}
                  onClick={() =>
                    onChange([
                      ...entrees,
                      { sheet: entree.sheet, kind: autre, columns: {}, capacity_unit: 'L', date_format: 'DMY' },
                    ])
                  }
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {autre === 'drivers' ? 'Lire aussi des chauffeurs' : 'Lire aussi des camions'}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default CorrespondanceColonnes;
